import React from 'react';
import { BarChart, Bar, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell } from 'recharts';

const SkillsSection = () => {
  const technicalSkills = [
    { name: 'Agile/SAFe', level: 95 },
    { name: 'Azure Cloud', level: 88 },
    { name: 'AEM', level: 85 },
    { name: 'Salesforce', level: 82 },
    { name: 'JIRA/Confluence', level: 92 },
    { name: 'API Integration', level: 80 },
  ];

  const leadershipSkills = [
    { skill: 'Team Leadership', value: 95 },
    { skill: 'Stakeholder Mgmt', value: 92 },
    { skill: 'Risk Management', value: 90 },
    { skill: 'Strategic Planning', value: 88 },
    { skill: 'Process Improvement', value: 85 },
    { skill: 'Change Management', value: 87 },
  ];

  const toolsAndTechnologies = [
    { category: 'Project Management', tools: ['JIRA', 'Confluence', 'Smartsheet', 'MS Project'] },
    { category: 'Cloud Platforms', tools: ['Azure', 'Salesforce', 'AWS'] },
    { category: 'Content Management', tools: ['Adobe AEM', 'Adobe Target', 'Adobe Campaign', 'Marketo'] },
    { category: 'Development', tools: ['API Integration', 'Microservices', 'CI/CD', 'SQL'] },
    { category: 'Analytics', tools: ['Tableau', 'Cognos', 'Power BI'] },
    { category: 'Payment Systems', tools: ['Zelle', 'Stripe', 'PCI DSS Compliance'] },
  ];

  const COLORS = ['#2563eb', '#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe', '#dbeafe'];

  return (
    <section id="skills" className="py-20 bg-gray-50" data-testid="skills-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-testid="skills-title">
            Skills & Expertise
          </h2>
          <div className="w-20 h-1 bg-blue-600 mx-auto"></div>
          <p className="mt-4 text-xl text-gray-600">A comprehensive overview of my technical and leadership capabilities</p>
        </div>

        {/* Charts Section */}
        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          {/* Technical Skills Bar Chart */}
          <div className="bg-white rounded-2xl shadow-lg p-8" data-testid="technical-skills-chart">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Technical Proficiency</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={technicalSkills} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" domain={[0, 100]} />
                <YAxis dataKey="name" type="category" width={120} />
                <Tooltip />
                <Bar dataKey="level" radius={[0, 8, 8, 0]}>
                  {technicalSkills.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Leadership Skills Radar Chart */}
          <div className="bg-white rounded-2xl shadow-lg p-8" data-testid="leadership-skills-chart">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Leadership Capabilities</h3>
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart data={leadershipSkills}>
                <PolarGrid />
                <PolarAngleAxis dataKey="skill" />
                <PolarRadiusAxis angle={90} domain={[0, 100]} />
                <Radar name="Proficiency" dataKey="value" stroke="#2563eb" fill="#3b82f6" fillOpacity={0.6} />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Tools & Technologies Grid */}
        <div className="bg-white rounded-2xl shadow-lg p-8" data-testid="tools-technologies">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Tools & Technologies</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {toolsAndTechnologies.map((category, index) => (
              <div key={index} className="space-y-4">
                <h4 className="text-lg font-semibold text-blue-600 border-b-2 border-blue-200 pb-2">
                  {category.category}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {category.tools.map((tool, toolIndex) => (
                    <span
                      key={toolIndex}
                      className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium hover:bg-blue-100 transition-colors"
                    >
                      {tool}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Methodologies */}
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-xl p-6 text-center">
            <h4 className="text-2xl font-bold mb-2">Agile</h4>
            <p className="text-blue-100">Scrum, Kanban, XP</p>
          </div>
          <div className="bg-gradient-to-br from-blue-700 to-blue-800 text-white rounded-xl p-6 text-center">
            <h4 className="text-2xl font-bold mb-2">SAFe</h4>
            <p className="text-blue-100">PI Planning, ART, Value Streams</p>
          </div>
          <div className="bg-gradient-to-br from-blue-800 to-blue-900 text-white rounded-xl p-6 text-center">
            <h4 className="text-2xl font-bold mb-2">Waterfall</h4>
            <p className="text-blue-100">Traditional Project Management</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SkillsSection;